import React, { useState, useEffect } from 'react';
import { Table, Form, Input, Button, Space, Tooltip, Card, Col, message } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import * as roleService from '../../services/role/roleService';
import RoleMenuAuthModal from './RoleMenuAuthModal';
import FilterPanel from '../../components/common/FilterPanel';
import './index.css';

const RoleManagement = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [menuAuthVisible, setMenuAuthVisible] = useState(false);
    const [currentRole, setCurrentRole] = useState(null);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`
    });
    const { t } = useTranslation();
    const { tableWrapRef, scrollY } = useTableScrollHeight();

    const columns = [
        {
            title: t('role.name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('role.code'),
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: t('role.description'),
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: t('role.actions'),
            key: 'actions',
            render: (record) => (
                <Space>
                    <Tooltip title={t('role.menuAuthorization')}>
                        <Button
                            type="link"
                            icon={<KeyOutlined />}
                            onClick={() => handleMenuAuth(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const fetchData = async (params = {}) => {
        setLoading(true);
        try {
            // 获取表单当前值
            const formValues = form.getFieldsValue();
            
            // 过滤掉空值和undefined值
            const filteredFormValues = Object.keys(formValues).reduce((acc, key) => {
                const value = formValues[key];
                if (value !== undefined && value !== null && value !== '') {
                    acc[key] = value;
                }
                return acc;
            }, {});

            const searchParams = {
                ...filteredFormValues,
                currentPage: params.currentPage || pagination.current,
                pageSize: params.pageSize || pagination.pageSize,
                type: "ROLE"
            };

            const response = await roleService.pageRoles(searchParams);

            if (response.success) {
                setDataSource(response.data.tableData);
                setPagination(prev => ({
                    ...prev,
                    current: response.data.paging?.currentPage || 1,
                    total: response.data.paging?.totalCount || 0
                }));
            } else {
                message.error(response.errorMessage || t('role.queryFailed'));
            }
        } catch (error) {
            console.error('Query failed:', error);
            message.error(t('role.queryFailedRetry'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSearch = (values) => {
        // 将搜索条件设置到表单中
        form.setFieldsValue(values);
        setPagination(prev => ({ ...prev, current: 1 }));
        // 使用传入的values进行查询
        fetchData({ currentPage: 1 });
    };

    const handleReset = () => {
        form.resetFields();
        setPagination(prev => ({ ...prev, current: 1 }));
        // 重置后延迟一下再查询，确保表单已经重置完成
        setTimeout(() => {
            fetchData({ currentPage: 1 });
        }, 0);
    };

    const handleMenuAuth = (role) => {
        setCurrentRole(role);
        setMenuAuthVisible(true);
    };

    const handleMenuAuthCancel = () => {
        setMenuAuthVisible(false);
        setCurrentRole(null);
    };

    const handleMenuAuthSuccess = () => {
        setMenuAuthVisible(false);
        setCurrentRole(null);
        fetchData(); // 刷新数据以获取最新的菜单权限信息
    };

    const handleTableChange = (paginationConfig) => {
        const newPagination = {
            ...pagination,
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize
        };
        setPagination(newPagination);
        fetchData({
            currentPage: paginationConfig.current,
            pageSize: paginationConfig.pageSize
        });
    };


    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <FilterPanel
                title={t('role.title')}
                onFilter={handleSearch}
                onReset={handleReset}
                initialValues={{}}
                className="role-search"
                form={form}
            >
                <Col span={6}>
                    <Form.Item name="name" label={t('role.name')}>
                        <Input placeholder={t('role.name') + t('common.search')} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="code" label={t('role.code')}>
                        <Input placeholder={t('role.code') + t('common.search')} />
                    </Form.Item>
                </Col>
            </FilterPanel>

            <Card className="role-table" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, overflow: 'hidden', padding: '0 24px', display: 'flex', flexDirection: 'column' }}>
                <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <Table
                        columns={columns}
                        dataSource={dataSource}
                        loading={loading}
                        pagination={pagination}
                        onChange={handleTableChange}
                        rowKey="id"
                        scroll={{ x: 1200, y: scrollY }}
                    />
                </div>
            </Card>

            <RoleMenuAuthModal
                visible={menuAuthVisible}
                role={currentRole}
                onCancel={handleMenuAuthCancel}
                onSuccess={handleMenuAuthSuccess}
            />
        </div>
    );
};

export default RoleManagement;